/**
 * Which profile an assistant or agent is currently acting as.
 *
 * Every section used to read the stored selection itself and invent its own
 * fallback - usually "the first profile I manage". That is why an agent
 * managing several leaders could answer a question as the wrong one: the
 * selection was simply absent, and the section quietly picked one.
 *
 * Picking a profile on someone's behalf is never safe when they hold more
 * than one, because the choice decides whose name goes on the reply. So this
 * resolves to a profile only when there is no doubt, and returns null instead
 * of guessing. Callers are expected to stop and ask.
 */

import { API_BASE_URL } from '@/lib/api';

export const ACTIVE_PROFILE_KEY = 'baraza_selected_profile_id';

/** The stored selection, or null. Safe during SSR. */
export function getStoredProfileId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(ACTIVE_PROFILE_KEY);
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function setActiveProfileId(id: number | string): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(ACTIVE_PROFILE_KEY, String(id));
}

/**
 * Switch the profile this account acts as.
 *
 * The server is the record; sessionStorage is only a local cache so the next
 * screen paints without waiting for a round trip. Storing it on the account
 * is what makes a switch survive a reload, a new tab and a different device,
 * and lets the API attribute a request that does not name a profile instead
 * of guessing or refusing.
 *
 * Throws if the server refuses — the caller should not pretend the switch
 * happened, because the next write would go out under the old profile.
 */
export async function switchActiveProfile(id: number | string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/auth/active-profile/`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ leader_profile: Number(id) }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.detail || 'Could not switch profile.');
  }
  setActiveProfileId(id);
}

/** What the server says this account is acting as, or null. */
export async function fetchActiveProfileId(): Promise<number | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/active-profile/`, {
      credentials: 'include',
    });
    if (!response.ok) return null;
    const body = await response.json();
    const id = body?.active_leader_profile?.id;
    if (typeof id === 'number') {
      // Keep the local cache in step so the next paint is instant.
      setActiveProfileId(id);
      return id;
    }
    clearActiveProfileId();
    return null;
  } catch {
    return null;
  }
}

export function clearActiveProfileId(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(ACTIVE_PROFILE_KEY);
}

/**
 * The profile to act as, given everything this person manages.
 *
 * Returns null when the answer is genuinely unknown - no stored selection, or
 * one that is stale, while managing several profiles. A stale selection is not
 * carried over: it may point at a profile that was handed on to someone else.
 */
export function resolveActiveProfileId(managedIds: number[]): number | null {
  const ids = (managedIds || []).filter((n) => Number.isFinite(n));
  if (ids.length === 0) return null;

  const stored = getStoredProfileId();
  if (stored !== null && ids.includes(stored)) return stored;

  // Exactly one profile: there is nothing to get wrong.
  if (ids.length === 1) return ids[0];

  return null;
}

/**
 * The profile to act as, asking the server when the tab does not know.
 *
 * Every dashboard page used to work this out for itself, and all of them did
 * it the same wrong way: read the id out of sessionStorage, and if it was not
 * there, quietly use the first profile the API happened to return. Two things
 * followed from that.
 *
 * The lookup was also gated on `role === 'agent'`, so an assistant or a leader
 * holding several profiles never had their choice read at all.
 *
 * And sessionStorage is per tab. Opening the dashboard in a second tab lost
 * the selection and silently fell back to someone else's profile - the pages
 * then showed that member's posts under this member's name, which reads as
 * "my posts have vanished".
 *
 * The server has known the answer all along; nothing asked it. Order here is
 * the tab's cache first because it is instant, then the account, then the
 * single profile that cannot be ambiguous - and null rather than a guess.
 */
export async function resolveActingProfileId(managedIds: number[]): Promise<number | null> {
  const ids = (managedIds || []).filter((n) => Number.isFinite(n));
  if (ids.length === 0) return null;

  const cached = resolveActiveProfileId(ids);
  if (cached !== null) return cached;

  const stored = await fetchActiveProfileId();
  if (stored !== null && ids.includes(stored)) return stored;

  return null;
}

/** Message to show when the active profile cannot be determined. */
export const CHOOSE_PROFILE_MESSAGE =
  'Choose which profile you are acting as before continuing.';

export interface ActingIdentity {
  /** The profile to act as, or null when acting personally / undecided. */
  profileId: number | null;
  /** Who to show. Never the account holder's own name for staff. */
  displayName: string;
  /** True when several profiles are held and none is selected. */
  needsChoice: boolean;
}

interface MinimalProfile {
  id: number;
  full_name?: string | null;
  name?: string | null;
}

/**
 * Who an action is performed as, and what to call them.
 *
 * "Asking as ..." used to print the signed-in account's own name, which for an
 * assistant or agent is both the wrong party and a person who is never named
 * on this site. What matters is the profile being acted for.
 *
 * @param profiles  the approved profiles this account holds
 * @param accountName  fallback for an ordinary member of the public
 * @param isStaff  true for a PA or agent, who must never be shown by name
 */
export function resolveActingIdentity(
  profiles: MinimalProfile[],
  accountName: string,
  isStaff: boolean,
): ActingIdentity {
  const held = (profiles || []).filter((p) => p && Number.isFinite(p.id));

  if (held.length === 0) {
    // Staff with nothing to act as must not fall back to their own name.
    return { profileId: null, displayName: isStaff ? '' : accountName, needsChoice: isStaff };
  }

  const activeId = resolveActiveProfileId(held.map((p) => p.id));
  if (activeId === null) {
    return { profileId: null, displayName: '', needsChoice: true };
  }

  const active = held.find((p) => p.id === activeId);
  return {
    profileId: activeId,
    displayName: (active?.full_name || active?.name || '').trim() || accountName,
    needsChoice: false,
  };
}
